import { Link } from "@tanstack/react-router";
import type { ClusterExplanation, ExplainArtifact, PrDraftArtifact, ReproArtifact } from "@backstory/protocol";
import { CopyButton } from "../../../components/ui";
import { downloadText } from "../hooks";
import { AiOutput, ArtifactMetaLine, Claim, CitationChips, CodeBlock, DiffView, type CitationTools } from "./common";

export function ExplainArtifactView({ a, tools }: { a: ExplainArtifact; tools?: CitationTools }) {
  return (
    <AiOutput className="session-explanation" mark="Generated from this session" testId="explain-artifact">
      <p style={{ fontWeight: 600, margin: "0 0 6px" }}>{a.headline}</p>
      <Claim label="Tried" text={a.whatUserTried.text} citations={a.whatUserTried.citations} tools={tools} evidenceFirst />
      <Claim label="Broke" text={a.whatBroke.text} citations={a.whatBroke.citations} tools={tools} evidenceFirst />
      <Claim label="Impact" text={a.evidenceOfImpact.text} citations={a.evidenceOfImpact.citations} tools={tools} evidenceFirst />
      <Claim label="Cause" text={a.likelyRootCause.text} citations={a.likelyRootCause.citations} tools={tools} confidence={a.likelyRootCause.confidence} evidenceFirst />
      {a.suggestedFixes.length > 0 && (
        <div className="claim">
          <strong>Suggested fixes</strong>
          <ul>
            {a.suggestedFixes.map((f, i) => (<li key={i}>{f}</li>))}
          </ul>
        </div>
      )}
      {a.codeLocations?.length ? (
        <div className="claim">
          <strong>Code</strong>
          <ul>
            {a.codeLocations.map((c, i) => (
              <li key={i} className="mono small">
                {c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.file}{c.line ? `:${c.line}` : ""}</a> : <>{c.file}{c.line ? `:${c.line}` : ""}</>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {(a.affectsOtherSessions || a.relatedTraceId) && (
        <div className="claim small">
          {a.affectsOtherSessions && (
            <Link to="/issues/$clusterId" params={{ clusterId: a.affectsOtherSessions.clusterId }} className="badge warn">
              affects {a.affectsOtherSessions.count7d} sessions this week
            </Link>
          )}
          {a.relatedTraceId && <span className="mono muted" style={{ marginLeft: 8 }}>trace {a.relatedTraceId.slice(0, 12)}…</span>}
        </div>
      )}
      <ArtifactMetaLine meta={a.meta} />
    </AiOutput>
  );
}

export function ReproView({ a }: { a: ReproArtifact }) {
  const stepsText = a.steps.map((s, i) => `${i + 1}. ${s}`).join("\n");
  return (
    <AiOutput testId="repro-artifact">
      {a.preconditions?.length ? (
        <div className="claim">
          <strong>Preconditions</strong>
          <ul>{a.preconditions.map((p, i) => (<li key={i}>{p}</li>))}</ul>
        </div>
      ) : null}
      <div className="claim">
        <strong>Steps to reproduce</strong>
        <ol style={{ margin: "4px 0", paddingLeft: 18 }}>
          {a.steps.map((s, i) => (<li key={i}>{s}</li>))}
        </ol>
        <CopyButton value={stepsText} label="Copy steps" />
      </div>
      <div className="claim">
        <strong>Playwright test</strong>
        <CodeBlock code={a.playwrightTest} filename="repro.spec.ts" language="typescript" onDownload={() => downloadText("repro.spec.ts", a.playwrightTest, "text/typescript")} />
      </div>
      <ArtifactMetaLine meta={a.meta} />
    </AiOutput>
  );
}

export function ClusterExplanationView({ a, sessionHref }: { a: ClusterExplanation; sessionHref: (id: string) => { to: string; params: Record<string, string>; search?: Record<string, unknown> } }) {
  return (
    <AiOutput testId="cluster-explanation">
      <p style={{ fontWeight: 600, margin: "0 0 6px" }}>{a.title}</p>
      <Claim label="Impact" text={a.impact} />
      <Claim label="Likely cause" text={a.likelyCause.text} confidence={a.likelyCause.confidence} />
      <CitationChips citations={a.likelyCause.citations} sessionHref={(c) => (a.sampleSessionIds[0] ? sessionHref(c.sessionId ?? a.sampleSessionIds[0]!) : null)} />
      {a.affectedReleases.length > 0 && <p className="claim small"><strong>Releases</strong>{a.affectedReleases.join(", ")}</p>}
      {a.suggestedFixes.length > 0 && (
        <div className="claim">
          <strong>Suggested fixes</strong>
          <ul>{a.suggestedFixes.map((f, i) => (<li key={i}>{f}</li>))}</ul>
        </div>
      )}
      {a.sampleSessionIds.length > 0 && (
        <div className="claim small">
          <strong>Evidence sessions</strong>
          {a.sampleSessionIds.slice(0, 6).map((id) => {
            const h = sessionHref(id);
            return (
              <Link key={id} to={h.to as never} params={h.params as never} search={(h.search ?? {}) as never} className="badge neutral" style={{ marginRight: 4 }}>
                {id.slice(0, 8)}…
              </Link>
            );
          })}
        </div>
      )}
      <ArtifactMetaLine meta={a.meta} />
    </AiOutput>
  );
}

export function PrDraftView({ a }: { a: PrDraftArtifact }) {
  return (
    <AiOutput testId="pr-draft">
      <p style={{ fontWeight: 600, margin: "0 0 4px" }}>{a.title}</p>
      <p className="small muted mono" style={{ margin: 0 }}>{a.repo} · {a.branch}</p>
      <p style={{ whiteSpace: "pre-wrap" }}>{a.body}</p>
      <DiffView diff={a.diff} />
      <div className="actions-row" style={{ marginTop: 8 }}>
        {a.prUrl ? (
          <a className="btn primary" href={a.prUrl} target="_blank" rel="noreferrer">Open pull request</a>
        ) : (
          <button type="button" className="btn" onClick={() => downloadText(`${a.branch}.patch`, a.diff, "text/x-diff")}>Download patch</button>
        )}
      </div>
      <p className="small muted">Human review is always required before merging. The draft cites the replay evidence it was built from.</p>
      <ArtifactMetaLine meta={a.meta} />
    </AiOutput>
  );
}
